import React from 'react';
import { APP_BRAND_NAME, Locale } from '../studioLocale';
import { StudioIcon, StudioIconName } from './StudioIcons';
import './StudioSidebar.css';

export type StudioViewMode = 'board' | 'canvas' | 'lanes';

interface ModeItem {
  label: string;
  hint: string;
  icon: StudioIconName;
}

interface StudioSidebarProps {
  locale: Locale;
  activeMode: StudioViewMode;
  busy: boolean;
  authenticated: boolean;
  dirty: boolean;
  elementsCount: number;
  connectionsCount: number;
  scriptReady: boolean;
  workflowStatus: string;
  modes: Record<StudioViewMode, ModeItem>;
  onModeChange: (mode: StudioViewMode) => void;
  onOpenDevices: () => void;
  onOpenStarter: () => void;
  onOpenStructure: () => void;
  onOpenScript: () => void;
  onSaveWorkspace: () => void;
  onAuthenticate: () => void;
  inviteCodeEnabled: boolean;
  inviteCode: string;
  onInviteCodeChange: (value: string) => void;
  onInviteLogin: () => void;
  canGenerateStructure: boolean;
  canGenerateScript: boolean;
  canSaveWorkspace: boolean;
}

const COPY = {
  zh: {
    studio: '叙事工作室',
    motif: '蝴蝶效应创作台',
    quote: '“戏剧是从书页中站起来、成为人的诗。”',
    attribution: '费德里科·加西亚·洛尔卡',
    nav: '工作台',
    actions: '动作',
    state: '状态',
    start: '创作向导',
    structure: '生成结构',
    script: '生成剧本',
    save: '保存工作区',
    online: '已登录',
    offline: '未登录',
    busy: '运行中',
    ready: '待命',
    dirty: '有未保存改动',
    clean: '已同步',
    elements: '节点',
    links: '关系',
    scriptReady: '剧本就绪',
    noScript: '未生成剧本',
  },
  en: {
    studio: 'Narrative Studio',
    motif: 'Butterfly Effect Studio',
    quote: '"Theatre is poetry that rises from the book and becomes human."',
    attribution: 'Federico Garcia Lorca',
    nav: 'Workbench',
    actions: 'Actions',
    state: 'State',
    start: 'Starter',
    structure: 'Generate structure',
    script: 'Generate script',
    save: 'Save workspace',
    online: 'Signed in',
    offline: 'Signed out',
    busy: 'Running',
    ready: 'Ready',
    dirty: 'Unsaved changes',
    clean: 'Synced',
    elements: 'Nodes',
    links: 'Links',
    scriptReady: 'Script ready',
    noScript: 'No script',
  },
} as const;

const StudioSidebar: React.FC<StudioSidebarProps> = ({
  locale,
  activeMode,
  busy,
  authenticated,
  dirty,
  elementsCount,
  connectionsCount,
  scriptReady,
  workflowStatus,
  modes,
  onModeChange,
  onOpenDevices,
  onOpenStarter,
  onOpenStructure,
  onOpenScript,
  onSaveWorkspace,
  onAuthenticate,
  inviteCodeEnabled,
  inviteCode,
  onInviteCodeChange,
  onInviteLogin,
  canGenerateStructure,
  canGenerateScript,
  canSaveWorkspace,
}) => {
  const text = COPY[locale];
  const modeItems = (['board', 'canvas', 'lanes'] as StudioViewMode[]).map((key) => ({
    key,
    ...modes[key],
  }));

  return (
    <aside className="studio-sidebar" aria-label={text.nav}>
      <div className="studio-sidebar__brand">
        <div className="studio-sidebar__mark">M</div>
        <div>
          <strong>{APP_BRAND_NAME}</strong>
          <span>{text.studio}</span>
        </div>
      </div>

      <div className="studio-sidebar__quote">
        <span>{text.motif}</span>
        <p>{text.quote}</p>
        <small>{text.attribution}</small>
      </div>

      <section className="studio-sidebar__section">
        <div className="studio-sidebar__label">{text.nav}</div>
        <div className="studio-sidebar__modes">
          <button className="studio-sidebar__mode studio-sidebar__director" onClick={onOpenDevices}><StudioIcon name="spark" /><span><strong>装置导演台</strong><small>增删改 · AI 影响 · 剧本对照</small></span></button>
          {modeItems.map((mode) => (
            <button
              key={mode.key}
              type="button"
              className={`studio-sidebar__mode ${activeMode === mode.key ? 'is-active' : ''}`}
              onClick={() => onModeChange(mode.key)}
            >
              <StudioIcon name={mode.icon} />
              <span>
                <strong>{mode.label}</strong>
                <small>{mode.hint}</small>
              </span>
            </button>
          ))}
        </div>
      </section>

      <section className="studio-sidebar__section">
        <div className="studio-sidebar__label">{text.actions}</div>
        <div className="studio-sidebar__actions">
          <button type="button" data-capability="authentication" onClick={onAuthenticate} disabled={busy}>
            <StudioIcon name="assistant" />
            <span>{authenticated ? (locale === 'zh' ? '退出登录' : 'Sign out') : (locale === 'zh' ? '访客登录' : 'Guest sign in')}</span>
          </button>
          {!authenticated && inviteCodeEnabled && (
            <div className="studio-sidebar__invite">
              <input
                aria-label={locale === 'zh' ? '访问码' : 'Access code'}
                value={inviteCode}
                onChange={(event) => onInviteCodeChange(event.target.value)}
                placeholder={locale === 'zh' ? '输入访问码' : 'Enter access code'}
              />
              <button type="button" data-capability="invite_authentication" onClick={onInviteLogin} disabled={busy || !inviteCode.trim()}>
                <StudioIcon name="library" />
                <span>{locale === 'zh' ? '访问码登录' : 'Access code sign in'}</span>
              </button>
            </div>
          )}
          <button type="button" data-capability="open_starter" onClick={onOpenStarter}>
            <StudioIcon name="spark" />
            <span>{text.start}</span>
          </button>
          <button type="button" data-capability="generate_structure" onClick={onOpenStructure} disabled={!canGenerateStructure}>
            <StudioIcon name="structure" />
            <span>{text.structure}</span>
          </button>
          <button type="button" data-capability="generate_script" onClick={onOpenScript} disabled={!canGenerateScript}>
            <StudioIcon name="script" />
            <span>{text.script}</span>
          </button>
          <button type="button" data-capability="save_workspace" onClick={onSaveWorkspace} disabled={!canSaveWorkspace}>
            <StudioIcon name="download" />
            <span>{text.save}</span>
          </button>
        </div>
      </section>

      <section className="studio-sidebar__section studio-sidebar__section--state">
        <div className="studio-sidebar__label">{text.state}</div>
        <div className="studio-sidebar__status">
          <span className={authenticated ? 'is-good' : 'is-muted'}>{authenticated ? text.online : text.offline}</span>
          <span className={busy ? 'is-live' : 'is-muted'}>{busy ? text.busy : text.ready}</span>
          <span className={dirty ? 'is-warn' : 'is-muted'}>{dirty ? text.dirty : text.clean}</span>
          <span>{elementsCount} {text.elements}</span>
          <span>{connectionsCount} {text.links}</span>
          <span>{scriptReady ? text.scriptReady : text.noScript}</span>
        </div>
        <p className="studio-sidebar__workflow">{workflowStatus}</p>
      </section>
    </aside>
  );
};

export default StudioSidebar;
